import React from 'react';
const { useState, useMemo } = React;
import { CURRENT_USER } from '../globals.js';
import { chargesCall } from '../chargesClient.js';
import { adminCall } from './diagnostic/shared.jsx';
import { coachCall } from './coaching/shared.jsx';
import { openPrepared } from './serviceStart/ServiceStartPanel.jsx';
import { clientPath, contractsOf } from '../clientPath.js';

// «Recorrido del cliente» on a contact's card (src/clientPath.js): request → quote → publication → acceptance →
// installments → payments and receipts → service, only through links that exist by id. Read only: opening it reads
// the charges (charges.mts › list) and the services of each accepted quote («Preparar inicio del servicio», read
// actions), and each step opens its own record. A missing link is said, never filled in; what this login cannot
// read is «no disponible», never empty. Nothing here writes.

const muted = { fontSize: 12, color: 'var(--globant-muted)' };
const MARK = { done: '●', missing: '○', pending: '◌', unknown: '?' };
const COLOR = { done: 'var(--globant-green)', missing: 'var(--globant-warning)', pending: 'var(--globant-muted)', unknown: 'var(--globant-muted)' };
const fmtDay = (d) => (d ? new Date(`${d}T12:00:00Z`).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '');

function openStep(open) {
  if (!open) return;
  if (open.page === 'accounts' && open.diagnosticId) { openPrepared('diagnostic', { id: open.diagnosticId, accountId: open.accountId }); return; }
  window.dispatchEvent(new CustomEvent('oike:navigate', { detail: open }));
}

const TABLE_TEXT = { diagnosticIntakes: 'solicitudes y cotizaciones', opportunities: 'oportunidades', landings: 'documentos publicados (fechas de publicación)' };

export default function ClientPath({ stakeholderId, intakes = [], opportunities = [], landings = [], unavailable = [] }) {
  const [open, setOpen] = useState(false);
  const [billing, setBilling] = useState(undefined); // undefined: not read yet · null: not available
  const [services, setServices] = useState({});      // chain id → { diagnostics, processes, accountId } | { error }
  const [loading, setLoading] = useState(false);
  const admin = CURRENT_USER?.role === 'admin' || !!globalThis.window?.__OIKE_DEMO__;

  const read = async () => {
    setLoading(true);
    // Payments: the administration only (charges.mts checks it); anyone else gets «no disponible»
    const money = admin ? chargesCall('list').catch(() => null) : Promise.resolve(null);
    const accepted = contractsOf(intakes, stakeholderId).filter(c => c.sale);
    const svc = {};
    await Promise.all(accepted.map(async (c) => {
      const [d, p] = await Promise.all([
        adminCall({ action: 'serviceStartDiagnostic', quoteId: c.sale.id }).catch(e => ({ error: e.message })),
        coachCall({ action: 'serviceStartCoaching', quoteId: c.sale.id }).catch(e => ({ error: e.message })),
      ]);
      // A side that cannot be read (not the coach, no permission) leaves the service «no disponible» only if both fail
      if (d?.error && p?.error) { svc[c.id] = { error: d.error }; return; }
      svc[c.id] = { diagnostics: d?.error ? [] : d.diagnostics || [], processes: p?.error ? [] : p.processes || [], accountId: d?.account?.id || d?.quote?.accountId || '', partial: !!(d?.error || p?.error) };
    }));
    setBilling(await money);
    setServices(svc);
    setLoading(false);
  };

  const path = useMemo(() => clientPath({ stakeholderId, intakes, opportunities, landings, billing, servicesByChain: services }), [stakeholderId, intakes, opportunities, landings, billing, services]);
  const nothing = !path.contracts.length && !path.requestsWithoutQuote.length && !path.opportunities.personal.length && !path.opportunities.ambiguous.length;

  return (
    <details open={open} onToggle={(e) => { const o = e.currentTarget.open; setOpen(o); if (o && billing === undefined && !loading) read(); }}
      style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 10, border: '1px solid var(--globant-border)' }}>
      <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Recorrido del cliente</summary>
      <div style={{ ...muted, margin: '4px 0 8px' }}>Solo lo vinculado por id a esta persona: solicitud, cotización, aceptación, cuotas, pagos y servicio. Si falta un vínculo, se dice; nada se une por nombre, empresa ni email. Solo lectura.</div>
      {unavailable.filter(k => TABLE_TEXT[k]).length > 0 && <div role="alert" style={{ ...muted, color: 'var(--globant-warning)' }}>No se pudieron leer: {[...new Set(unavailable.filter(k => TABLE_TEXT[k]))].map(k => TABLE_TEXT[k]).join(', ')}. El recorrido puede estar incompleto: lo que falta de esas partes no significa que no exista.</div>}
      {loading && <div role="status" style={muted}>Cargando…</div>}
      {nothing && !loading && !unavailable.some(k => TABLE_TEXT[k]) && <div style={muted}>Todavía no hay solicitudes, cotizaciones ni oportunidades vinculadas a esta persona.</div>}
      {path.contracts.map(c => {
        const partial = services[c.id]?.partial;
        return (
          <div key={c.id} style={{ borderTop: '1px solid var(--globant-border)', padding: '8px 0' }}>
            <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>{c.title}</div>
            <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 3 }}>
              {c.steps.map((s, i) => (
                <li key={`${s.key}-${i}`} style={{ display: 'grid', gridTemplateColumns: '16px 1fr auto', gap: 8, alignItems: 'baseline', fontSize: 12 }}>
                  <span aria-hidden="true" style={{ color: COLOR[s.state] }}>{MARK[s.state]}</span>
                  <span>
                    <span className="sr-only">{s.state === 'done' ? 'Hecho: ' : s.state === 'missing' ? 'Falta: ' : s.state === 'unknown' ? 'No disponible: ' : 'Pendiente: '}</span>
                    {s.text}{s.day ? <span style={muted}> · {fmtDay(s.day)}</span> : null}
                  </span>
                  {s.open ? <button type="button" className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={() => openStep(s.open)}>Abrir</button> : <span />}
                </li>
              ))}
            </ol>
            {partial && <div style={{ ...muted, marginTop: 4 }}>Parte de los servicios no se pudo leer con tu perfil (diagnósticos o acompañamiento).</div>}
          </div>
        );
      })}
      {path.requestsWithoutQuote.length > 0 && (
        <div style={{ borderTop: '1px solid var(--globant-border)', padding: '8px 0', fontSize: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Solicitudes sin cotización</div>
          {path.requestsWithoutQuote.map(r => (
            <div key={r.id} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
              <span style={{ flex: '1 1 200px' }}>{r.name}{r.day ? <span style={muted}> · {fmtDay(r.day)}</span> : null}</span>
              <button type="button" className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={() => openStep({ page: 'requests' })}>Abrir Solicitudes</button>
            </div>
          ))}
        </div>
      )}
      {(path.opportunities.personal.length > 0 || path.opportunities.ambiguous.length > 0) && (
        <div style={{ borderTop: '1px solid var(--globant-border)', padding: '8px 0', fontSize: 12 }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>Oportunidades</div>
          {path.opportunities.personal.map(o => (
            <div key={o.id}>{o.name}{o.stage ? <span style={muted}> · {o.stage}</span> : null}<span style={muted}> · {o.via === 'contact' ? 'vinculada a esta persona' : 'creada desde una solicitud suya'}</span></div>
          ))}
          {path.opportunities.ambiguous.map(o => (
            <div key={o.id}>{o.name}<span style={{ ...muted, color: 'var(--globant-warning)' }}> · con varios contactos: no se atribuye a esta persona (revisar en Pipeline)</span></div>
          ))}
        </div>
      )}
      {billing !== undefined && !loading && (
        <button type="button" className="action-btn btn-ghost" style={{ fontSize: 11 }} onClick={read}>Volver a leer</button>
      )}
    </details>
  );
}
